<template>
  <div class="report-section report-section-resource-list">
    <div v-if="reportData.options?.name || reportData.name" class="section-title">{{ reportData.options?.name || reportData.name }}</div>
    <div v-if="reportData.options?.description || reportData.description" class="section-description">{{ reportData.options?.description || reportData.description }}</div>
    <div class="resource-list-controls" v-if="isTemplateEdit">
      <!-- 资源类型选择 -->
      <span class="control-item">
        <span class="control-label mr-2">{{ $t('report.resource_type') }}:</span>
        <base-select
          v-model="selectedResourceType"
          :options="availableResources"
          name-key="label"
          filterable
          :select-props="{ placeholder: $t('report.select_resource_type'), style: 'width: 200px;' }"
          @change="handleResourceTypeChange" />
      </span>
      <!-- TopN 选择 -->
      <span class="control-item">
        <span class="control-label">Top:</span>
        <a-select
          v-model="selectedTopN"
          style="width: 120px; margin-left: 8px;"
          @change="handleTopNChange">
          <a-select-option :value="5">5</a-select-option>
          <a-select-option :value="10">10</a-select-option>
          <a-select-option :value="20">20</a-select-option>
        </a-select>
      </span>
      <!-- 列配置 -->
      <span v-if="availableColumns.length > 0" class="control-item">
        <span class="control-label">{{ $t('report.columns') }}:</span>
        <a-popover
          placement="bottomLeft"
          trigger="click"
          :overlay-style="{ maxWidth: '800px' }"
          overlay-class-name="columns-popover"
          style="margin-left: 8px;">
          <template slot="title">
            <div class="d-flex align-items-center justify-content-between">
              <span>{{ $t('report.columns') }}</span>
              <a-checkbox
                :indeterminate="indeterminate"
                @change="handleCheckAllChange"
                :checked="checkAll">{{ $t('common.checkAll') }}</a-checkbox>
            </div>
          </template>
          <template slot="content">
            <div class="columns-popover-content">
              <a-checkbox-group v-model="selectedColumnFields" @change="handleColumnsChange" style="display: block;">
                <a-row>
                  <draggable
                    handle=".drag-icon"
                    ghost-class="ghost"
                    v-model="draggableColumns"
                    @end="handleDragEnd">
                    <transition-group type="transition" name="flip-list" tag="div" style="display: contents;">
                      <a-col
                        :key="col.field"
                        v-for="col in draggableColumns"
                        :span="8"
                        class="mb-2 checkbox-item">
                        <div class="d-flex align-items-center justify-content-between">
                          <a-checkbox :value="col.field" class="text-truncate checkbox-property">
                            <span :title="col.title">{{ col.title }}</span>
                          </a-checkbox>
                          <a-icon type="drag" class="drag-icon pr-3" />
                        </div>
                      </a-col>
                    </transition-group>
                  </draggable>
                </a-row>
              </a-checkbox-group>
            </div>
          </template>
          <a-button type="link" style="padding: 0;">
            {{ $t('report.select_columns') }} ({{ selectedColumnFields.length }}/{{ filteredColumnsForList.length }})
          </a-button>
        </a-popover>
      </span>
    </div>
    <!-- 资源列表 -->
    <div v-if="selectedResourceType && currentResource" class="resource-list-container">
      <a-alert v-if="isTemplateEdit" type="info" :message="$t('report.resource_list_tip')" />
      <component
        :is="currentResource.component"
        :id="`report-resource-list-${selectedResourceType}`"
        :key="componentKey || selectedResourceType"
        :get-params="getParams"
        :show-searchbox="false"
        :show-group-actions="false"
        :is-template="true"
        :is-template-edit="isTemplateEdit"
        :template-params="templateParams"
        :template-list-columns="templateListColumns"
        :template-limit="selectedTopN"
        @resTemplateTotal="handleResTemplateTotal"
        @update:columns="loadColumns"
        ref="listComponent" />
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import storage from '@/utils/storage'
import { getSearchMaps } from '@/constants/globalSearch'
// 导入所有列表组件
import GpuList from '@Compute/views/gpu/components/List'
import IDPList from '@IAM/views/idp/components/List'
import DomainList from '@IAM/views/domains/components/List'
import ProjectList from '@IAM/views/projects/components/List'
import UserList from '@IAM/views/user/components/List'
import GroupList from '@IAM/views/group/components/List'
import RoleList from '@IAM/views/role/components/List'
import PolicyList from '@IAM/views/policy/components/List'
import VmInstanceList from '@Compute/views/vminstance/components/List'
import ServertemplateList from '@Compute/views/servertemplate/components/List'
import BaremetalList from '@Compute/views/baremetal/components/List'
import ImageList from '@Compute/views/image/components/List'
import HostImageList from '@Compute/views/host-image/components/List'
import DiskList from '@Compute/views/disk/components/List'
import InstanceSnapshotList from '@Compute/views/snapshot-instance/components/List'
import SnapshotList from '@Compute/views/snapshot/components/List'
import SnapshotPolicyList from '@Compute/views/snapshotpolicy/components/List'
import SecgroupList from '@Compute/views/secgroup/components/List'
import EipList from '@Network/views/eip/components/List'
import NetworkList from '@Network/views/network/components/List'
import HostList from '@Compute/views/host/components/List'
import PhysicalmachineList from '@Compute/views/physicalmachine/components/List'
import RDSList from '@DB/views/rds/components/List'
import RedisList from '@DB/views/redis/components/List'
import ServerRecoveryList from '@Compute/views/server-recovery/components/List'
import DiskRecoveryList from '@Compute/views/disk-recovery/components/List'
import ImageRecoveryList from '@Compute/views/image-recovery/components/List'
import VPCList from '@Network/views/vpc/components/List'
import BlockStorageList from '@Storage/views/blockstorage/components/List'
import BucketStorageList from '@Storage/views/bucket/components/List'
import NatList from '@Network/views/nats/components/List'
import DnsZoneList from '@Network/views/dns-zone/components/List'
import DnsRecordSetList from '@Network/views/dns-recordset/components/List'
import LbList from '@Network/views/lb/components/List'
import LbaclsList from '@Network/views/lbacls/components/List'
import LbcertList from '@Network/views/lbcerts/components/List'
import RouteTableList from '@Network/views/route-table/components/List'
import CloudaccountList from '@Cloudenv/views/cloudaccount/components/List'
import VpcPeerConnectList from '@Network/views/vpc-peer-connect/components/List'
import VpcNetworkList from '@Network/views/vpc-network/components/List'
import MongoDBList from '@DB/views/mongodb/components/List'
import KafkaList from '@Middleware/views/kafka/components/List'
import ElasticSearchList from '@Middleware/views/elasticsearch/components/List'
import CdnDomainList from '@Network/views/cdn/components/List'
import WafList from '@Network/views/waf/components/List'
import FileSystemList from '@Storage/views/file-system/components/List'
import VmContainerInstanceList from '@Compute/views/vminstance-container/components/List'
import WireList from '@Network/views/wire/components/List'
import DeadlyResourceList from '@Compute/views/deadly-resource/components/List'
import AlertResourceList from '@Monitor/views/alertresource/components/List'
import BillOriginResTypeList from '@Bill/views/originrestype/components/List'
import AllProcessList from '@System/views/workflow/all-process/components/List'
import MeProcessList from '@System/views/workflow/me-process/components/List'
import MePartakeProcessList from '@System/views/workflow/me-partake/components/List'
import AnalysisAlarmList from '@Bill/views/analysis-alarm/components/List'
import ApprovedDoneProcessList from '@System/views/workflow/approved-done/components/List'
import ApprovalStartProcessList from '@System/views/workflow/approval-start/components/List'
import AnalysisAlarmHistoryList from '@Bill/views/analysis-alarm/sidepage/History'
import { uuid } from '@/utils/utils'
import { getResourceMaps } from '@Reports/constants'

export default {
  name: 'ResourceList',
  components: {
    draggable,
    VmInstanceList,
    ServertemplateList,
    BaremetalList,
    ImageList,
    HostImageList,
    DiskList,
    SnapshotList,
    SnapshotPolicyList,
    InstanceSnapshotList,
    SecgroupList,
    EipList,
    NetworkList,
    HostList,
    PhysicalmachineList,
    RDSList,
    RedisList,
    ServerRecoveryList,
    DiskRecoveryList,
    ImageRecoveryList,
    VPCList,
    BlockStorageList,
    BucketStorageList,
    IDPList,
    DomainList,
    ProjectList,
    UserList,
    GroupList,
    RoleList,
    PolicyList,
    NatList,
    DnsZoneList,
    DnsRecordSetList,
    LbList,
    LbaclsList,
    LbcertList,
    CloudaccountList,
    RouteTableList,
    VpcPeerConnectList,
    VpcNetworkList,
    MongoDBList,
    KafkaList,
    ElasticSearchList,
    CdnDomainList,
    WafList,
    FileSystemList,
    GpuList,
    VmContainerInstanceList,
    WireList,
    DeadlyResourceList,
    AlertResourceList,
    AllProcessList,
    AnalysisAlarmList,
    ApprovedDoneProcessList,
    ApprovalStartProcessList,
    MeProcessList,
    MePartakeProcessList,
    BillOriginResTypeList,
    AnalysisAlarmHistoryList,
  },
  props: {
    reportData: {
      type: Object,
      default: () => ({}),
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    isTemplateEdit: {
      type: Boolean,
      default: false,
    },
    scopeParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    const allMaps = getSearchMaps({}, getResourceMaps(this))
    // 过滤有权限的资源
    const availableResources = Object.values(allMaps).filter(resource => {
      return resource.hasPermission && !this.$isScopedPolicyMenuHidden(`sub_hidden_menus.${resource.menu_key}`)
    })
    const queryParams = this.reportData.queryParams || this.reportData.options?.queryParams || {}
    // 处理 columns 数据：如果是旧格式（字符串数组），转换为新格式（对象数组）
    let selectedColumns = queryParams.columns || []
    if (selectedColumns.length > 0 && typeof selectedColumns[0] === 'string') {
      // 旧格式：['field1', 'field2']，转换为新格式：[{field: 'field1'}, {field: 'field2'}]
      selectedColumns = selectedColumns.map(field => ({ field }))
    }
    // 如果 queryParams 中有 columns 且包含 width，设置到 localStorage
    if (queryParams.columns && queryParams.columns.length > 0) {
      const storageKey = `__oc_LIST_report-resource-list-${queryParams.resourceType || ''}__`
      if (storageKey && queryParams.resourceType) {
        const storageConfig = {}
        queryParams.columns.forEach(col => {
          const field = typeof col === 'string' ? col : col.field
          const width = typeof col === 'object' && col.width ? col.width : undefined
          if (field && width) {
            storageConfig[field] = { width }
          }
        })
        if (Object.keys(storageConfig).length > 0) {
          storage.set(storageKey, storageConfig)
        }
      }
    }
    return {
      allResourceMaps: allMaps,
      availableResources,
      selectedResourceType: queryParams.resourceType || (availableResources.length > 0 ? availableResources[0].id : null),
      selectedTopN: queryParams.topN || 20,
      selectedColumns, // 新格式：[{field: 'field1', width: 100}, {field: 'field2'}]
      draggableColumns: [], // 用于拖拽的列数据
      availableColumns: [],
      loadColumnsAttempts: 0,
      hiddenColumns: [],
      autoHiddenFilterKey: '',
      isResourceTypeChanged: false, // 标记资源类型是否已切换
      isMounted: false, // 标记组件是否已挂载
      componentKey: '', // 组件 key，只在资源类型变化时更新
    }
  },
  computed: {
    templateParams () {
      if (this.isTemplateEdit) {
        return {
          ...this.reportData.queryParams || this.reportData.options?.queryParams || {},
          topN: this.selectedTopN,
        }
      }
      return this.reportData.queryParams || this.reportData.options?.queryParams || {}
    },
    currentResource () {
      if (!this.selectedResourceType) return null
      // 先尝试直接用 selectedResourceType 作为 key 查找
      if (this.allResourceMaps[this.selectedResourceType]) {
        return this.allResourceMaps[this.selectedResourceType]
      }
      // 如果找不到，遍历 allResourceMaps 查找 id 匹配的资源
      return Object.values(this.allResourceMaps).find(resource => resource.id === this.selectedResourceType)
    },
    getParams () {
      if (!this.currentResource) return {}
      const commonParams = this.currentResource.params?.common || {}
      const ret = {
        ...commonParams,
        $t: uuid(),
        limit: this.selectedTopN,
        scope: this.$store.getters.scope,
      }
      if (this.scopeParams.project_id) {
        ret.project_id = this.scopeParams.project_id
      }
      if (this.scopeParams.domain_id) {
        ret.domain_id = this.scopeParams.domain_id
      }
      return ret
    },
    filteredColumnsForList () {
      // 如果没有可用列，返回 null（让列表组件使用默认列）
      if (!this.availableColumns.length) {
        return null
      }
      // 先过滤掉 hidden 为 true 的列
      let filteredColumns = this.availableColumns.filter(col => {
        // 检查 hidden 属性，如果是函数则调用，如果是布尔值则直接判断
        if (typeof col.hidden === 'function') {
          return !col.hidden()
        }
        return !col.hidden
      })
      // 应用 autoHiddenFilterKey 过滤：所有列字段需剔除 autoHiddenFilterKey 隐藏的字段
      filteredColumns = filteredColumns.filter(col => {
        if (this.autoHiddenFilterKey && this.$isScopedPolicyMenuHidden(`${this.autoHiddenFilterKey}.${col.hiddenField || col.field}`)) {
          return false
        }
        return true
      })
      // 应用 hiddenColumns 过滤：选中列中隐藏 hiddenColumns 包含的字段
      if (this.hiddenColumns && this.hiddenColumns.length > 0) {
        filteredColumns = filteredColumns.filter(col => {
          return !this.hiddenColumns.includes(col.field)
        })
      }
      // 始终返回所有过滤后的列，不根据 selectedColumns 过滤
      return filteredColumns.map(col => {
        const newCol = {
          ...col,
          sortable: false,
        }
        // 清除固定宽度，但保留 minWidth，让表格自动计算
        if (newCol.width) {
          delete newCol.width
        }
        // 确保有最小宽度
        if (!newCol.minWidth) {
          newCol.minWidth = 100
        }
        return newCol
      })
    },
    // 用于传递给表格组件的列（根据 selectedColumns 过滤）
    templateListColumns () {
      const filtered = this.filteredColumnsForList
      if (!filtered || !filtered.length) {
        return null
      }
      // 如果没有选中列，返回所有过滤后的列
      if (!this.selectedColumns.length) {
        return filtered
      }
      // 获取选中的字段列表
      const selectedFields = this.selectedColumns.map(col => typeof col === 'string' ? col : col.field)
      // 返回选中的列（在过滤后的列中），并应用列宽和顺序
      const selectedCols = filtered.filter(col => {
        return selectedFields.includes(col.field)
      })
      // 按照 selectedColumns 的顺序排序
      return selectedFields.map(field => {
        const col = selectedCols.find(c => c.field === field)
        if (!col) return null
        return { ...col }
      }).filter(Boolean)
    },
    // 选中的列字段列表（用于 checkbox-group）
    selectedColumnFields: {
      get () {
        return this.selectedColumns.map(col => typeof col === 'string' ? col : col.field)
      },
      set (fields) {
        // 保持 selectedColumns 的顺序和宽度信息
        const fieldMap = new Map(this.selectedColumns.map(col => {
          const field = typeof col === 'string' ? col : col.field
          return [field, col]
        }))
        // 按照新的字段顺序重新排列，保留宽度信息
        this.selectedColumns = fields.map(field => {
          const existing = fieldMap.get(field)
          if (existing && typeof existing === 'object') {
            return existing
          }
          return { field }
        })
      },
    },
    columnsKey () {
      // 计算选中列在所有列中的index组成的字符串
      if (!this.availableColumns.length || !this.selectedColumns.length) {
        return ''
      }
      const selectedFields = this.selectedColumns.map(col => typeof col === 'string' ? col : col.field)
      const indexes = []
      this.availableColumns.forEach((col, index) => {
        if (selectedFields.includes(col.field)) {
          indexes.push(index)
        }
      })
      return indexes.join('')
    },
    // 全选状态：是否所有列都被选中
    checkAll () {
      const allFields = this.draggableColumns.map(col => col.field)
      const selectedFields = this.selectedColumnFields
      return selectedFields.length > 0 && selectedFields.length === allFields.length
    },
    // 半选状态：部分列被选中
    indeterminate () {
      const allFields = this.draggableColumns.map(col => col.field)
      const selectedFields = this.selectedColumnFields
      return selectedFields.length > 0 && selectedFields.length < allFields.length
    },
  },
  watch: {
    selectedResourceType: {
      handler (newVal, oldVal) {
        // immediate: true 时，oldVal 是 undefined，这是初始化调用
        // 只在组件挂载后且资源类型真正变化时才调用 loadColumns
        if (oldVal === undefined) {
          // 初始化时，设置稳定的 key，等待组件挂载后再调用
          this.componentKey = `${newVal}-init`
          this.$nextTick(() => {
            if (this.isMounted) {
              this.loadColumns()
            }
          })
        } else if (newVal !== oldVal) {
          // 资源类型真正变化时，更新 key
          this.componentKey = `${newVal}-${Date.now()}`
          this.$nextTick(() => {
            this.loadColumns()
          })
        }
      },
      immediate: true,
    },
    selectedColumns: {
      handler () {
        this.applyColumnFilter()
      },
      deep: true,
    },
  },
  mounted () {
    this.isMounted = true
  },
  methods: {
    handleResTemplateTotal (total) {
      this.$emit('resourceListHidden', total === 0)
    },
    handleResourceTypeChange () {
      // 标记资源类型已切换，这样 loadColumns 会使用新资源类型的默认列
      this.isResourceTypeChanged = true
      // 清空当前选择，等待新资源类型的列加载
      this.selectedColumns = []
      this.availableColumns = []
      this.hiddenColumns = []
      this.autoHiddenFilterKey = ''
      // 注意：不需要在这里调用 loadColumns，因为 selectedResourceType 的 watch 会自动触发
    },
    handleTopNChange () {
      // topN 变化时，列表会自动刷新（通过 getParams 的变化）
    },
    handleColumnsChange (val) {
      // 限制：至少保留一个字段被选中
      // val 是 @change 事件传递的新值数组
      const newSelectedFields = val || []
      if (newSelectedFields.length === 0) {
        // 如果尝试取消所有字段，保留第一个可用字段
        const firstField = this.draggableColumns.length > 0 ? this.draggableColumns[0].field : null
        if (firstField) {
          this.$message.warning(this.$t('report.at_least_one_column_required'))
          // 使用 $nextTick 确保在下一个 tick 更新，避免与 v-model 冲突
          this.$nextTick(() => {
            this.selectedColumnFields = [firstField]
          })
          return
        }
      }
      this.applyColumnFilter()
      // 更新 draggableColumns，只保留选中的列
      this.updateDraggableColumns()
    },
    handleCheckAllChange (e) {
      const allFields = this.draggableColumns.map(col => col.field)
      if (e.target.checked) {
        // 全选：选中所有字段
        this.selectedColumnFields = allFields
      } else {
        // 取消全选：限制至少保留一个字段
        if (allFields.length > 0) {
          this.$message.warning(this.$t('report.at_least_one_column_required'))
          // 保留第一个字段
          this.selectedColumnFields = [allFields[0]]
        }
      }
    },
    handleDragEnd () {
      // 拖拽结束后，只更新已选中列的顺序，不改变选中状态
      const selectedFields = this.selectedColumns.map(col => typeof col === 'string' ? col : col.field)
      const newOrder = this.draggableColumns.map(col => col.field)
      const fieldMap = new Map(this.selectedColumns.map(col => {
        const field = typeof col === 'string' ? col : col.field
        return [field, col]
      }))
      // 只对已选中的列按照拖拽后的顺序重新排列，保留宽度信息
      // 未选中的列不加入 selectedColumns
      this.selectedColumns = newOrder
        .filter(field => selectedFields.includes(field)) // 只保留已选中的列
        .map(field => {
          const existing = fieldMap.get(field)
          if (existing && typeof existing === 'object') {
            return existing
          }
          return { field }
        })
    },
    updateDraggableColumns () {
      // 更新 draggableColumns，显示所有可用列，保持原始顺序
      // 如果 draggableColumns 已有数据，保持其顺序（用户可能已经拖拽调整过）
      if (this.draggableColumns.length > 0) {
        // 保持现有顺序，只更新字段信息
        const existingFields = new Set(this.draggableColumns.map(col => col.field))
        const newFields = new Set(this.filteredColumnsForList.map(col => col.field))
        // 添加新出现的列到末尾
        const newCols = this.filteredColumnsForList.filter(col => !existingFields.has(col.field))
        // 移除已不存在的列
        this.draggableColumns = this.draggableColumns
          .filter(col => newFields.has(col.field))
          .map(col => {
            // 更新列信息，保持最新
            const latestCol = this.filteredColumnsForList.find(c => c.field === col.field)
            return latestCol || col
          })
        // 添加新列到末尾
        if (newCols.length > 0) {
          this.draggableColumns = [...this.draggableColumns, ...newCols]
        }
      } else {
        // 首次加载，使用 filteredColumnsForList 的原始顺序
        this.draggableColumns = [...this.filteredColumnsForList]
      }
    },
    applyColumnFilter () {
      if (!this.$refs.listComponent || !this.selectedColumns.length) {
        return
      }
      const listComponent = this.$refs.listComponent
      // 尝试修改子组件的 columns
      if (listComponent.columns && Array.isArray(listComponent.columns)) {
        // 如果 columns 是 computed 属性，可能需要通过其他方式修改
        // 注意：直接修改 computed 属性可能不会生效
        // 这是一个限制，可能需要列表组件支持 columns prop
        // 目前列过滤功能可能无法完全实现，但提供了配置界面
      }
    },
    loadColumns () {
      // 使用 setTimeout 确保动态组件完全渲染
      setTimeout(() => {
        if (!this.$refs.listComponent) {
          // 如果组件还未挂载，再次尝试（最多尝试5次）
          if (this.loadColumnsAttempts < 5) {
            this.loadColumnsAttempts = (this.loadColumnsAttempts || 0) + 1
            this.$nextTick(() => {
              this.loadColumns()
            })
          }
          return
        }
        this.loadColumnsAttempts = 0
        const listComponent = this.$refs.listComponent
        if (listComponent) {
          // 尝试多种方式获取 columns
          let columns = null
          if (listComponent.templateListColumnsKey && listComponent[listComponent.templateListColumnsKey]) {
            columns = listComponent[listComponent.templateListColumnsKey]
          } else if (listComponent.columns) {
            // 方式1: 直接从组件获取 columns（computed 属性）
            columns = listComponent.columns
          } else if (listComponent.$data && listComponent.$data.columns) {
            // 方式2: 从 data 获取
            columns = listComponent.$data.columns
          }
          if (columns) {
            // 获取所有列（包括 hidden 的），因为我们需要在 filteredColumnsForList 中统一处理
            this.availableColumns = Array.isArray(columns) ? columns : []
            // 从列表组件实例中获取 hiddenColumns 和 autoHiddenFilterKey
            if (listComponent.list) {
              if (listComponent.list.config) {
                this.hiddenColumns = listComponent.list.config.hiddenColumns || []
              }
              // autoHiddenFilterKey 在创建 list 时传入，可能存储在组件的 data 中
              // 尝试从多个位置获取
              if (listComponent.$data && listComponent.$data.list && listComponent.$data.list.autoHiddenFilterKey !== undefined) {
                this.autoHiddenFilterKey = listComponent.$data.list.autoHiddenFilterKey
              } else if (listComponent.autoHiddenFilterKey !== undefined) {
                this.autoHiddenFilterKey = listComponent.autoHiddenFilterKey
              } else {
                // 如果无法获取，尝试从列表组件的 computed 或其他属性中获取
                // 某些列表组件可能将 autoHiddenFilterKey 作为 data 属性
                const listData = listComponent.$data
                if (listData && listData.autoHiddenFilterKey !== undefined) {
                  this.autoHiddenFilterKey = listData.autoHiddenFilterKey
                } else {
                  this.autoHiddenFilterKey = ''
                }
              }
            }
            // 获取所有过滤后的列（用于后续选择）
            const filteredCols = this.availableColumns.filter(col => {
              // 先过滤掉 hidden 为 true 的列
              if (typeof col.hidden === 'function') {
                if (col.hidden()) return false
              } else if (col.hidden) {
                return false
              }
              // 应用 autoHiddenFilterKey 过滤
              if (this.autoHiddenFilterKey && this.$isScopedPolicyMenuHidden(`${this.autoHiddenFilterKey}.${col.hiddenField || col.field}`)) {
                return false
              }
              // 应用 hiddenColumns 过滤
              if (this.hiddenColumns && this.hiddenColumns.includes(col.field)) {
                return false
              }
              return true
            })
            // 如果资源类型已切换，使用新资源类型的默认列（所有过滤后的列）
            if (this.isResourceTypeChanged) {
              this.isResourceTypeChanged = false
              // 直接设置所有过滤后的列
              const allFields = filteredCols
                .map(col => col.field)
                .filter(field => field && field !== '_action' && field !== '_action_placeholder')
              // 使用数组替换确保响应式更新，转换为新格式
              this.selectedColumns.splice(0, this.selectedColumns.length, ...allFields.map(field => ({ field })))
            } else {
              // 获取 queryParams 中的 columns
              const queryParams = this.reportData.queryParams || this.reportData.options?.queryParams || {}
              const queryColumns = queryParams.columns || []
              // 处理旧格式的 columns（字符串数组）
              let columnsToUse = queryColumns.length > 0 ? queryColumns : (this.reportData.columns || [])
              if (columnsToUse.length > 0 && typeof columnsToUse[0] === 'string') {
                // 旧格式，转换为新格式
                columnsToUse = columnsToUse.map(field => ({ field }))
              }
              // 如果 queryParams 中没有配置 columns，且 reportData 中也没有配置 columns，默认选择所有列（应用过滤后）
              if (!columnsToUse.length) {
                this.selectedColumns = filteredCols
                  .map(col => ({ field: col.field }))
                  .filter(col => col.field && col.field !== '_action' && col.field !== '_action_placeholder')
              } else {
                // 只保留 columnsToUse 中存在的列，并恢复列宽信息
                const fieldMap = new Map(columnsToUse.map(col => {
                  const field = typeof col === 'string' ? col : col.field
                  return [field, typeof col === 'string' ? { field } : col]
                }))
                // 从 localStorage 读取列宽并应用到 selectedColumns
                const storageKey = `__oc_LIST_report-resource-list-${this.selectedResourceType}__`
                const storageConfig = storage.get(storageKey) || {}
                this.selectedColumns = filteredCols
                  .map(col => col.field)
                  .filter(field => field && field !== '_action' && field !== '_action_placeholder')
                  .filter(field => fieldMap.has(field))
                  .map(field => {
                    const colConfig = fieldMap.get(field)
                    // 如果 colConfig 中没有 width，尝试从 localStorage 读取
                    if (typeof colConfig === 'object' && !colConfig.width && storageConfig[field] && storageConfig[field].width) {
                      return { ...colConfig, width: storageConfig[field].width }
                    }
                    return colConfig
                  })
              }
            }
            // 更新 draggableColumns
            this.updateDraggableColumns()
          } else {
            // 如果无法获取 columns，清空可用列
            this.availableColumns = []
            this.selectedColumns = []
            this.hiddenColumns = []
            this.autoHiddenFilterKey = ''
          }
        }
      }, 200)
    },
    getTemplateParams () {
      // 保存列配置，包括字段和列宽
      // 列宽从 localStorage 中读取
      const storageKey = `__oc_LIST_report-resource-list-${this.selectedResourceType}__`
      const storageConfig = storage.get(storageKey) || {}
      const columns = this.selectedColumns.map(col => {
        const field = typeof col === 'string' ? col : col.field
        // 优先从 localStorage 读取列宽
        let width
        if (storageConfig[field] && storageConfig[field].width) {
          width = storageConfig[field].width
        }
        if (width) {
          return { field, width }
        }
        return { field }
      })
      const params = {
        resourceType: this.selectedResourceType,
        topN: this.selectedTopN,
        columns,
      }
      if (this.selectedResourceType === 'alertResource') {
        params.time = this.$refs.listComponent.time
      }
      if (this.selectedResourceType === 'analysisAlarmHistory') {
        params.alarmId = this.$refs.listComponent.alarmId
      }
      return params
    },
  },
}
</script>

<style lang="less" scoped>
.report-section-resource-list {
  max-width: 695px;
  .section-title {
    color: #000;
    font-size: 18px;
    font-weight: bold;
    padding-bottom: 5px;
    border-bottom: 2px solid rgba(0, 0, 0, 0.5);
    margin-bottom: 15px;
  }
  .section-description {
    color: #666;
    font-size: 14px;
    margin-bottom: 15px;
  }
  .resource-list-controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
    .control-item {
      display: flex;
      align-items: center;
      .control-label {
        white-space: nowrap;
      }
    }
  }
  .resource-list-container {
    margin-top: 16px;
  }
}
</style>

<style lang="less">
.columns-popover {
  .ant-popover-inner-content {
    max-width: 800px;
  }
  .columns-popover-content {
    max-width: 800px;
    .checkbox-item {
      margin-bottom: 8px;
      .d-flex {
        width: 100%;
      }
      ::v-deep {
        .ant-checkbox-wrapper {
          display: flex;
          align-items: center;
          flex: 1;
          .ant-checkbox {
            margin-top: 3px;
            & + span {
              flex: 1;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
        .checkbox-property {
          padding-right: 15px;
        }
      }
      .drag-icon {
        visibility: hidden;
        cursor: pointer;
        color: #999;
        &:hover {
          color: #1890ff;
        }
      }
      &:hover {
        .drag-icon {
          visibility: visible !important;
        }
      }
    }
  }
}
.flip-list-move {
  transition: transform 0.5s;
}
.ghost {
  opacity: 0.7;
  background: #e6f7ff;
  ::v-deep {
    label span {
      color: #1890ff;
    }
  }
}
</style>
